import React, { useEffect, useMemo, useState } from "react";
import EmployeeLayoutV2 from "../../components/EmployeeLayoutV2";
import {
    getBusinessById,
    getEmployeeData,
    getTipsByEmployee,
} from "../../services/firestoreService";
import { useAuth } from "../../context/AuthProvider";

const getTimestampDate = (value) => {
    if (!value) return null;
    if (value?.seconds) return new Date(value.seconds * 1000);
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateTime = (value) => {
    const date = getTimestampDate(value);
    return date ? date.toLocaleString() : "-";
};

const StarIcon = ({ filled }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill={filled ? "#e8a20e" : "none"} stroke={filled ? "#e8a20e" : "#d4d4d4"} strokeWidth="2">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
);

export default function EmployeeTipsV2() {
    const { userData } = useAuth();

    const [business, setBusiness] = useState(null);
    const [employee, setEmployee] = useState(null);
    const [tips, setTips] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            if (!userData?.businessID || !userData?.employeeID) return;

            const [businessData, employeeData, tipsData] = await Promise.all([
                getBusinessById(userData.businessID),
                getEmployeeData(userData.businessID, userData.employeeID),
                getTipsByEmployee(userData.businessID, userData.employeeID),
            ]);

            setBusiness(businessData || null);
            setEmployee(employeeData || null);
            setTips(Array.isArray(tipsData) ? tipsData : []);
        };

        fetchData();
    }, [userData]);

    const sortedTips = useMemo(
        () => [...tips].sort((a, b) => (getTimestampDate(b.createdAt) || 0) - (getTimestampDate(a.createdAt) || 0)),
        [tips]
    );

    const totalAmount = sortedTips.reduce((sum, tip) => sum + Number(tip.amount || 0), 0);
    const ratedTips = sortedTips.filter((tip) => Number.isFinite(Number(tip.rating)));
    const avgRating = ratedTips.length
        ? ratedTips.reduce((sum, tip) => sum + Number(tip.rating), 0) / ratedTips.length
        : 0;

    return (
        <EmployeeLayoutV2>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=JetBrains+Mono:wght@400;500&display=swap');

                .tips-v2 * { box-sizing: border-box; margin: 0; padding: 0; }
                .tips-v2 {
                    --surface: #ffffff;
                    --surface-raised: #f0f0f5;
                    --border: #e8e8ee;
                    --text-primary: #1a1a2e;
                    --text-secondary: #6b6b82;
                    --text-muted: #9b9bb0;
                    --accent: #0d7c5f;
                    --accent-light: #e8f5f0;
                    --green: #0d7c5f;
                    --amber: #c47a0a;
                    --font: 'DM Sans', -apple-system, sans-serif;
                    --mono: 'JetBrains Mono', monospace;
                    font-family: var(--font);
                    color: var(--text-primary);
                    min-height: 100vh;
                }
                .tips-v2 .container {
                    width: 100%;
                    max-width: 100%;
                    margin: 0 auto;
                    padding: 32px 0 80px;
                }
                .tips-v2 .header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 26px;
                    gap: 12px;
                    flex-wrap: wrap;
                }
                .tips-v2 .title {
                    font-size: 28px;
                    font-weight: 700;
                    letter-spacing: -0.5px;
                }
                .tips-v2 .subtitle {
                    margin-top: 4px;
                    font-size: 14px;
                    color: var(--text-secondary);
                }
                .tips-v2 .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 14px;
                    margin-bottom: 28px;
                }
                @media (max-width: 700px) {
                    .tips-v2 .stats-grid { grid-template-columns: repeat(2, 1fr); }
                }
                .tips-v2 .stat-card {
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 16px;
                    padding: 18px;
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }
                .tips-v2 .stat-label {
                    font-size: 12px;
                    font-weight: 500;
                    color: var(--text-muted);
                    text-transform: uppercase;
                    letter-spacing: 0.7px;
                }
                .tips-v2 .stat-value {
                    font-size: 24px;
                    font-weight: 700;
                    letter-spacing: -0.8px;
                    font-family: var(--mono);
                    color: var(--green);
                }
                .tips-v2 .stat-suffix {
                    font-size: 12px;
                    color: var(--text-muted);
                }
                .tips-v2 .list-shell {
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 18px;
                    overflow: hidden;
                }
                .tips-v2 .list-header {
                    background: var(--surface-raised);
                    border-bottom: 1px solid var(--border);
                    padding: 14px 18px;
                    font-size: 13px;
                    font-weight: 600;
                    color: var(--text-secondary);
                }
                .tips-v2 .tip-item {
                    padding: 16px 18px;
                    border-bottom: 1px solid var(--border);
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 12px;
                }
                .tips-v2 .tip-item:last-child { border-bottom: none; }
                .tips-v2 .left {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    min-width: 0;
                }
                .tips-v2 .amount {
                    font-family: var(--mono);
                    font-weight: 700;
                    font-size: 20px;
                    letter-spacing: -0.6px;
                    color: var(--green);
                }
                .tips-v2 .feedback {
                    color: var(--text-secondary);
                    font-size: 13px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 460px;
                }
                .tips-v2 .right {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;
                    gap: 6px;
                    flex-shrink: 0;
                }
                .tips-v2 .date {
                    font-size: 12px;
                    color: var(--text-muted);
                    text-align: right;
                }
                .tips-v2 .stars {
                    display: flex;
                    gap: 2px;
                }
                .tips-v2 .empty {
                    padding: 16px;
                    font-size: 14px;
                    color: var(--text-secondary);
                }
                @media (max-width: 700px) {
                    .tips-v2 .tip-item {
                        flex-direction: column;
                        align-items: flex-start;
                    }
                    .tips-v2 .right {
                        align-items: flex-start;
                    }
                    .tips-v2 .date {
                        text-align: left;
                    }
                    .tips-v2 .feedback {
                        white-space: normal;
                    }
                }
            `}</style>

            <div className="tips-v2">
                <div className="container">
                    <header className="header">
                        <div>
                            <h1 className="title">My Tips</h1>
                            <p className="subtitle">
                                {business?.name || userData?.businessID || "Business"} · {employee?.firstName || "Employee"}
                            </p>
                        </div>
                    </header>

                    <div className="stats-grid">
                        <div className="stat-card">
                            <span className="stat-label">Total Tips</span>
                            <span className="stat-value">{sortedTips.length}</span>
                            <span className="stat-suffix">transactions</span>
                        </div>
                        <div className="stat-card">
                            <span className="stat-label">Total Amount</span>
                            <span className="stat-value">€{totalAmount.toFixed(2)}</span>
                            <span className="stat-suffix">all-time</span>
                        </div>
                        <div className="stat-card">
                            <span className="stat-label">Avg Tip</span>
                            <span className="stat-value">
                                €{sortedTips.length ? (totalAmount / sortedTips.length).toFixed(2) : "0.00"}
                            </span>
                            <span className="stat-suffix">per tip</span>
                        </div>
                        <div className="stat-card">
                            <span className="stat-label">Avg Rating</span>
                            <span className="stat-value">{avgRating ? avgRating.toFixed(1) : "0.0"}/5</span>
                            <span className="stat-suffix">rated tips only</span>
                        </div>
                    </div>

                    <div className="list-shell">
                        <div className="list-header">Tip History</div>
                        {sortedTips.length === 0 ? (
                            <div className="empty">No tips found.</div>
                        ) : (
                            sortedTips.map((tip) => (
                                <article key={tip.id} className="tip-item">
                                    <div className="left">
                                        <span className="amount">+€{Number(tip.amount || 0).toFixed(2)}</span>
                                        <span className="feedback">{tip.feedback || "No feedback"}</span>
                                    </div>

                                    <div className="right">
                                        <div className="stars">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <StarIcon key={star} filled={star <= Number(tip.rating || 0)} />
                                            ))}
                                        </div>
                                        <span className="date">{formatDateTime(tip.createdAt)}</span>
                                    </div>
                                </article>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </EmployeeLayoutV2>
    );
}
